import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  mongodbAtlasAiAccessTokenPrefix,
  mongodbAtlasAiClientSecretSecretId,
  mongodbAtlasAiConnectorName,
} from "@p0security/integrations-mongodb-atlas-ai-shared/secret-manager";
import type { MongodbAtlasAiComponents } from "@p0security/integrations-mongodb-atlas-ai-shared/types";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  MongodbAtlasAiComponents["credential"]
>["secretManager"];

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-mongodb-atlas-ai-gcloud",
  digest:
    "sha-fced489@sha256:53d208e62c4e3419902f3488fbb651f2f70435d1d9705093c7c2345b56d7477d",
} as const;

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-mongodb-atlas-ai-aws",
  digest:
    "sha-fced489@sha256:2c756fce711e0f04ff7d19cf2ec95955d418b27aafe75d9e2789f760e01c4b08",
} as const;

const awsClientSecretSecretId = (project: string) =>
  secretProviderFor("aws").replacer(
    mongodbAtlasAiClientSecretSecretId(project)
  );

export const awsStoreClientSecret = (
  project: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  shellLines(
    `printf '%s' "$ATLAS_CLIENT_SECRET"`,
    "| aws secretsmanager put-secret-value",
    `--secret-id ${awsClientSecretSecretId(project)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file:///dev/stdin"
  );

export const awsTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  awsConnectorTf({
    names: { resource: "mongodb_atlas", label: "MongoDB Atlas" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: mongodbAtlasAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      mongodbAtlasAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "client_secret",
      itemTfId: tfId(itemId),
      secretId: awsClientSecretSecretId(itemId),
      label: "client secret",
    },
  });

const gcpClientSecretSecretId = (project: string) =>
  secretProviderFor("gcp").replacer(
    mongodbAtlasAiClientSecretSecretId(project)
  );

export const gcpStoreClientSecret = (
  project: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  shellLines(
    `printf '%s' "$ATLAS_CLIENT_SECRET"`,
    `| gcloud secrets versions add ${gcpClientSecretSecretId(project)}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const gcpTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  gcpConnectorTf({
    names: {
      resource: "mongodb_atlas",
      role: "MongodbAtlas",
      label: "MongoDB Atlas",
    },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      mongodbAtlasAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "client_secret",
      itemTfId: tfId(itemId),
      secretId: gcpClientSecretSecretId(itemId),
      label: "client secret",
    },
  });
